/**
 * Admin access requests - pending and rejected Microsoft users waiting for
 * console access.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState, renderUnavailableState } from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderRelativeTime } from "../components/dateTime";
import { adminStatusLabel, EMPTY } from "../utils/format";
import { confirmAction } from "../components/confirmDialog";
import { showToast } from "../components/toast";
import { ApiError } from "../services/apiClient";
import { approveAccessRequest, listAccessRequests, rejectAccessRequest } from "../services/auth";
import type { AccessRequest } from "../types/api";

export class AccessRequestsPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading admin access requests…")]);

  mount(outlet: HTMLElement): void {
    const refreshButton = el(
      "button",
      { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.load() },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Admin access requests",
          subtitle: "Users waiting for approval to use this admin console.",
          actions: [refreshButton],
        }),
        el("div", { className: "panel panel--standard" }, [this.body]),
      ])
    );

    void this.load();
  }

  destroy(): void {
    // No timers.
  }

  private async load(): Promise<void> {
    try {
      const rows = await listAccessRequests();
      if (!rows.length) {
        mount(this.body, renderEmptyState("No pending or rejected admin access requests."));
        return;
      }
      mount(this.body, this.renderTable(rows));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 503)) {
        mount(this.body, renderUnavailableState(err.message));
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private renderTable(rows: AccessRequest[]): HTMLElement {
    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["User"]),
          el("th", { scope: "col", className: "data-table__col--status" }, ["Status"]),
          el("th", { scope: "col", className: "data-table__col--relative" }, ["Requested"]),
          el("th", { scope: "col", className: "data-table__col--actions-wide data-table__actions-col" }, [
            "Actions",
          ]),
        ]),
      ]),
      el(
        "tbody",
        {},
        rows.map((row) =>
          el("tr", {}, [
            el("td", { className: "data-table__cell-wrap" }, [
              el("div", {}, [row.displayName || row.email || EMPTY]),
              el("div", { className: "data-table__cell-sub" }, [
                row.email
                  ? row.email
                  : el("span", { className: "mono" }, [row.entraOid ?? EMPTY]),
              ]),
            ]),
            el("td", {}, [
              renderStatusText(
                adminStatusLabel(row.status),
                row.status === "pending" ? "accent" : "danger"
              ),
            ]),
            el("td", {}, [renderRelativeTime(row.requestedAt)]),
            el("td", { className: "data-table__actions-cell data-table__col--actions-wide" }, [
              this.renderActions(row),
            ]),
          ])
        )
      ),
    ]);
    return el("div", { className: "data-table-wrap" }, [table]);
  }

  private renderActions(row: AccessRequest): HTMLElement {
    const approve = el(
      "button",
      {
        className: "btn btn-primary btn-sm",
        type: "button",
        disabled: row.status === "approved",
        onclick: () => void this.approve(row),
      },
      ["Approve"]
    );
    const reject = el(
      "button",
      {
        className: "btn btn-danger-ghost btn-sm",
        type: "button",
        disabled: row.status === "rejected",
        onclick: () => void this.reject(row),
      },
      ["Reject"]
    );
    return el("div", { className: "row" }, [approve, reject]);
  }

  private async approve(row: AccessRequest): Promise<void> {
    const ok = await confirmAction({
      title: "Approve admin access",
      message: `Allow ${row.email || row.displayName || "this user"} to use the admin console?`,
      confirmLabel: "Approve",
    });
    if (!ok) return;
    try {
      await approveAccessRequest(row.id);
      showToast("Admin access approved.");
      await this.load();
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }

  private async reject(row: AccessRequest): Promise<void> {
    const ok = await confirmAction({
      title: "Reject admin access",
      message: `Deny ${row.email || row.displayName || "this user"} admin console access?`,
      confirmLabel: "Reject",
      danger: true,
    });
    if (!ok) return;
    try {
      await rejectAccessRequest(row.id);
      showToast("Admin access rejected.");
      await this.load();
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
